import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const SITE_NAME = 'Nextcoders';
const SITE_URL = 'https://nextcoders.work';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const DEFAULT_TITLE = 'Nextcoders | Digitalization, automation and business solutions';
const DEFAULT_DESCRIPTION =
  'Nextcoders helps companies improve operations with digitalization, automation, CRM, AI, telephony and business apps.';

const ROUTE_META = {
  '/': {
    title: {
      es: 'Nextcoders | Digitalizacion, automatizacion y soluciones empresariales',
      en: 'Nextcoders | Digitalization, automation and business solutions',
      pt: 'Nextcoders | Digitalizacao, automacao e solucoes empresariais',
    },
    description: {
      es: 'Impulsamos empresas con digitalizacion, automatizacion, CRM, IA, telefonia y soluciones operativas.',
      en: 'We help companies grow with digitalization, automation, CRM, AI, telephony and operational solutions.',
      pt: 'Impulsionamos empresas com digitalizacao, automacao, CRM, IA, telefonia e solucoes operacionais.',
    },
  },
  '/crm': {
    title: {
      es: 'CRM | Advisory, arquitectura y automatizacion comercial',
      en: 'CRM | Advisory, architecture and commercial automation',
      pt: 'CRM | Advisory, arquitetura e automacao comercial',
    },
    description: {
      es: 'Asesoria CRM de Nextcoders para ordenar procesos comerciales, automatizaciones, tableros e integraciones con criterio operativo.',
      en: 'Nextcoders CRM advisory to structure commercial processes, automations, dashboards and integrations with operational discipline.',
      pt: 'Advisory CRM da Nextcoders para organizar processos comerciais, automacoes, paineis e integracoes com criterio operacional.',
    },
  },
  '/apps': {
    title: {
      es: 'Apps | Herramientas y productos de Nextcoders',
      en: 'Apps | Tools and products by Nextcoders',
      pt: 'Apps | Ferramentas e produtos da Nextcoders',
    },
    description: {
      es: 'Explora las aplicaciones y productos publicos de Nextcoders para operaciones, productividad y automatizacion.',
      en: 'Explore the public apps and products by Nextcoders for operations, productivity and automation.',
      pt: 'Explore os aplicativos e produtos publicos da Nextcoders para operacoes, produtividade e automacao.',
    },
  },
  '/blog': {
    title: {
      es: 'Blog | Ideas, transformacion digital y operaciones',
      en: 'Blog | Ideas, digital transformation and operations',
      pt: 'Blog | Ideias, transformacao digital e operacoes',
    },
    description: {
      es: 'Contenido sobre transformacion digital, automatizacion, CRM, IA, operaciones y crecimiento empresarial.',
      en: 'Content about digital transformation, automation, CRM, AI, operations and business growth.',
      pt: 'Conteudo sobre transformacao digital, automacao, CRM, IA, operacoes e crescimento empresarial.',
    },
  },
  '/sitemap': {
    title: {
      es: 'Mapa del sitio | Nextcoders',
      en: 'Sitemap | Nextcoders',
      pt: 'Mapa do site | Nextcoders',
    },
    description: {
      es: 'Indice de las principales paginas publicas de Nextcoders.',
      en: 'Index of the main public pages of Nextcoders.',
      pt: 'Indice das principais paginas publicas da Nextcoders.',
    },
  },
  '/nexbot': {
    title: {
      es: 'NexBot | Automatizacion conversacional con Bitrix24',
      en: 'NexBot | Conversational automation with Bitrix24',
      pt: 'NexBot | Automacao conversacional com Bitrix24',
    },
    description: {
      es: 'Plataforma de automatizacion conversacional integrada con Bitrix24 para capturar, clasificar y transferir conversaciones.',
      en: 'Conversational automation platform integrated with Bitrix24 to capture, classify and transfer conversations.',
      pt: 'Plataforma de automacao conversacional integrada ao Bitrix24 para capturar, classificar e transferir conversas.',
    },
  },
  '/nexbot-contact': {
    title: {
      es: 'NexBot | Contacto del desarrollador',
      en: 'NexBot | Contact developer',
      pt: 'NexBot | Contato do desenvolvedor',
    },
    description: {
      es: 'Pagina de contacto del desarrollador para revisiones, soporte e implementacion de NexBot.',
      en: 'Developer contact page for NexBot reviews, support, and implementation questions.',
      pt: 'Pagina de contato do desenvolvedor para revisoes, suporte e implementacao do NexBot.',
    },
  },
  '/nexbot-demo': {
    title: {
      es: 'NexBot | Comenzar',
      en: 'NexBot | Get started',
      pt: 'NexBot | Comecar',
    },
    description: {
      es: 'Pagina para comenzar con NexBot, revisar informacion legal y acceder a soporte.',
      en: 'Get-started page for NexBot with legal information and support links.',
      pt: 'Pagina para comecar com o NexBot, revisar detalhes legais e acessar suporte.',
    },
  },
  '/nexbot-eula': {
    title: {
      es: 'NexBot | EULA',
      en: 'NexBot | EULA',
      pt: 'NexBot | EULA',
    },
    description: {
      es: 'Acuerdo de licencia de usuario final de NexBot.',
      en: 'End-user license agreement for NexBot.',
      pt: 'Contrato de licenca do usuario final do NexBot.',
    },
  },
  '/nexbot-privacidad': {
    title: {
      es: 'NexBot | Politica de privacidad',
      en: 'NexBot | Privacy policy',
      pt: 'NexBot | Politica de privacidade',
    },
    description: {
      es: 'Politica de privacidad y tratamiento de datos de NexBot.',
      en: 'Privacy policy and data handling information for NexBot.',
      pt: 'Politica de privacidade e tratamento de dados do NexBot.',
    },
  },
  '/nextshot': {
    title: {
      es: 'NextShot | Captura, edita y exporta en segundos',
      en: 'NextShot | Capture, edit and export in seconds',
      pt: 'NextShot | Capture, edite e exporte em segundos',
    },
    description: {
      es: 'Herramienta para capturar pantalla, editar rapidamente y exportar con privacidad y velocidad.',
      en: 'Tool to capture your screen, edit quickly and export with privacy and speed.',
      pt: 'Ferramenta para capturar a tela, editar rapidamente e exportar com privacidade e velocidade.',
    },
  },
  '/watok': {
    title: {
      es: 'WATOK | Gestion profesional de WhatsApp API',
      en: 'WATOK | Professional WhatsApp API management',
      pt: 'WATOK | Gestao profissional da API do WhatsApp',
    },
    description: {
      es: 'Integra WhatsApp API, organiza conversaciones WABA y mantiene control operativo con enfoque profesional y seguro.',
      en: 'Integrate WhatsApp API, orchestrate WABA conversations and keep operational control with a professional, secure approach.',
      pt: 'Integre a API do WhatsApp, organize conversas WABA e mantenha o controle operacional com uma abordagem profissional e segura.',
    },
  },
  '/watok-security-policy': {
    title: {
      es: 'WATOK | Politica de seguridad',
      en: 'WATOK | Security policy',
      pt: 'WATOK | Politica de seguranca',
    },
    description: {
      es: 'Compromisos de seguridad y tratamiento responsable de datos para WATOK.',
      en: 'Security commitments and responsible data handling for WATOK.',
      pt: 'Compromissos de seguranca e tratamento responsavel de dados para o WATOK.',
    },
  },
  '/whatsapp': {
    title: {
      es: 'WhatsApp | Integracion y automatizacion empresarial',
      en: 'WhatsApp | Enterprise integration and automation',
      pt: 'WhatsApp | Integracao e automacao empresarial',
    },
    description: {
      es: 'Centraliza conversaciones, automatiza respuestas y conecta WhatsApp con tus procesos y CRM.',
      en: 'Centralize conversations, automate replies and connect WhatsApp with your processes and CRM.',
      pt: 'Centralize conversas, automatize respostas e conecte o WhatsApp aos seus processos e CRM.',
    },
  },
  '/telefonia': {
    title: {
      es: 'Telefonia | Llamadas, control y trazabilidad',
      en: 'Telephony | Calls, control and traceability',
      pt: 'Telefonia | Chamadas, controle e rastreabilidade',
    },
    description: {
      es: 'Integra telefonia empresarial con CRM, seguimiento y automatizacion operativa.',
      en: 'Integrate business telephony with CRM, tracking and operational automation.',
      pt: 'Integre telefonia empresarial com CRM, acompanhamento e automacao operacional.',
    },
  },
  '/reclutamiento-de-personal': {
    title: {
      es: 'Reclutamiento de personal | Procesos mas claros y rapidos',
      en: 'Recruitment | Clearer and faster hiring processes',
      pt: 'Recrutamento | Processos mais claros e rapidos',
    },
    description: {
      es: 'Optimiza reclutamiento, seguimiento de candidatos y documentacion con procesos mas ordenados.',
      en: 'Optimize recruiting, candidate tracking and documentation with better structured processes.',
      pt: 'Otimize recrutamento, acompanhamento de candidatos e documentacao com processos mais organizados.',
    },
  },
  '/clinicas': {
    title: {
      es: 'Clinicas | Operacion, trazabilidad y control',
      en: 'Clinics | Operations, traceability and control',
      pt: 'Clinicas | Operacao, rastreabilidade e controle',
    },
    description: {
      es: 'Mejora la operacion de clinicas con trazabilidad, seguridad, automatizacion y control operativo.',
      en: 'Improve clinic operations with traceability, security, automation and operational control.',
      pt: 'Melhore a operacao de clinicas com rastreabilidade, seguranca, automacao e controle operacional.',
    },
  },
  '/renacimiento': {
    title: {
      es: 'Renacimiento | Diagnostico y transformacion operativa',
      en: 'Renacimiento | Diagnosis and operational transformation',
      pt: 'Renacimiento | Diagnostico e transformacao operacional',
    },
    description: {
      es: 'Diagnostico visual y transformacion operativa para pasar del caos a procesos claros y medibles.',
      en: 'Visual diagnosis and operational transformation to move from chaos to clear and measurable processes.',
      pt: 'Diagnostico visual e transformacao operacional para sair do caos e chegar a processos claros e mensuraveis.',
    },
  },
};

const HIDDEN_ROUTES = new Set([
  '/notex',
  '/nextpdf',
  '/infood',
  '/posti',
  '/posti-privacidad',
  '/posti-terminos',
  '/posti-eula',
  '/nexia',
]);

const pickValue = (value, lang) => {
  if (!value) return undefined;
  if (typeof value === 'string') return value;
  return value[lang] || value.es || value.en || Object.values(value)[0];
};

const normalizePathname = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
};

const SEOManager = () => {
  const location = useLocation();
  const { i18n } = useTranslation();

  const lang = (i18n.language || 'es').split('-')[0];
  const pathname = normalizePathname(location.pathname || '/');
  const meta = ROUTE_META[pathname] || {};
  const isHidden = HIDDEN_ROUTES.has(pathname);
  const canonicalPath = pathname === '/nexia' ? '/nexbot' : pathname;
  const canonicalUrl = `${SITE_URL}${canonicalPath}`;
  const title = pickValue(meta.title, lang) || DEFAULT_TITLE;
  const description = pickValue(meta.description, lang) || DEFAULT_DESCRIPTION;
  const robots = isHidden ? 'noindex, nofollow' : 'index, follow';

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content={robots} />
      <meta
        name="facebook-domain-verification"
        content="frrfhswnlb8gf2p8tfdrdevs8kh3x0"
      />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={DEFAULT_IMAGE} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={DEFAULT_IMAGE} />
    </Helmet>
  );
};

export default SEOManager;
